import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { useAuthStore } from "@/client/stores/auth";

import {
  LmsChapter,
  LmsChapterQuizResult,
  LmsChapterResourceItem,
  LmsCourse,
  LmsCourseChatMessage,
  LmsCourseFeedback,
  LmsEnrollPayload,
  LmsLesson,
  LmsProgressPayload,
  LmsQuiz,
} from "./types";

const LMS_API_PREFIX =
  (import.meta.env.VITE_LMS_API_PREFIX as string | undefined) ??
  "/integration/external-service/lms";
const DEFAULT_LMS_THUMBNAIL = "/LMS.png";

const LMS_QUERY_KEYS = {
  all: ["lms"] as const,
  courses: (userScope: string) => ["lms", "courses", userScope] as const,
  course: (courseId: string, userScope: string) => ["lms", "course", courseId, userScope] as const,
};

type AnyRecord = Record<string, any>;

const buildUserScope = (user: AnyRecord | null) => {
  const identifiers = [user?.rollNo, user?.studentId, user?.id, user?.email]
    .map((value) =>
      String(value ?? "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);
  return [...new Set(identifiers)].join("|") || "anonymous";
};

const resolveStudentIdentifiers = (): string[] => {
  const user = useAuthStore.getState().user as AnyRecord | null;
  const values = [
    user?.rollNo,
    user?.roll_no,
    user?.studentId,
    user?.student_id,
    user?.id,
    user?._id,
    user?.email,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  return [...new Set(values)];
};

const resolveStudentVisibility = () => {
  const user = useAuthStore.getState().user as AnyRecord | null;
  const orgId = String(
    user?.organization?.id ??
      user?.organization?.org_id ??
      user?.organization?.organization_id ??
      user?.organization_id ??
      user?.organizationId ??
      user?.org_id ??
      user?.orgId ??
      "",
  ).trim();
  const departmentId = String(
    user?.organization?.department?.id ??
      user?.organization?.department_id ??
      user?.departmentId ??
      user?.department_id ??
      "",
  ).trim();

  const rawStudentGroupIds = Array.isArray(user?.studentGroupIds)
    ? user.studentGroupIds
    : Array.isArray(user?.student_group_ids)
      ? user.student_group_ids
      : [];
  const studentGroupIds = [
    ...new Set(
      rawStudentGroupIds.map((value: unknown) => String(value ?? "").trim()).filter(Boolean),
    ),
  ];

  return { orgId, departmentId, studentGroupIds };
};

const resolveStudentGroupIds = async (
  identifiers: string[],
  fallbackIds: string[],
): Promise<string[]> => {
  // Group resolution is handled server-side in /integration/external-service/lms.
  // Client only sends already-known ids from auth profile when available.
  if (fallbackIds.length > 0) return fallbackIds;
  if (identifiers.length === 0) return [];
  return [];
};

const pickArray = (source: AnyRecord | undefined, keys: string[]) => {
  if (!source) return [];
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const pickObject = (source: AnyRecord | undefined, keys: string[]) => {
  if (!source) return;
  for (const key of keys) {
    const value = source[key];
    if (value && typeof value === "object" && !Array.isArray(value)) return value;
  }
  return;
};

const normalizeLesson = (lesson: AnyRecord): LmsLesson => ({
  id: String(lesson.id ?? lesson._id ?? lesson.lessonId ?? `${lesson.title}-${lesson.order ?? 0}`),
  title: lesson.title ?? lesson.name ?? "Untitled Lesson",
  description: lesson.description ?? lesson.summary ?? "",
  durationInMinutes: Number(
    lesson.durationInMinutes ?? lesson.duration ?? lesson.duration_minutes ?? 0,
  ),
  order: Number(lesson.order ?? lesson.sequence ?? 0),
  videoUrl: lesson.videoUrl ?? lesson.video_url ?? lesson.streamUrl ?? lesson.contentUrl ?? "",
  videoSourceType: lesson.videoSourceType ?? lesson.sourceType ?? "uploaded",
  isPreview: Boolean(lesson.isPreview ?? lesson.preview),
});

const normalizeCourse = (course: AnyRecord): LmsCourse => {
  const chaptersInput = pickArray(course, ["chapters", "modules", "sections"]);
  const chapters: LmsChapter[] = chaptersInput.map((chapter: AnyRecord, chapterIndex: number) => {
    const lessonInput = pickArray(chapter, ["lessons", "items", "content"]);
    const resources: LmsChapterResourceItem[] = pickArray(chapter, ["resources"]).map(
      (resource: AnyRecord, resourceIndex: number): LmsChapterResourceItem => ({
        id: String(
          resource.id ?? resource._id ?? `resource-${chapterIndex + 1}-${resourceIndex + 1}`,
        ),
        type:
          String(resource.type ?? "").toLowerCase() === "url"
            ? "url"
            : String(resource.type ?? "").toLowerCase() === "file"
              ? "file"
              : "text",
        title: String(resource.title ?? ""),
        value: String(resource.value ?? ""),
      }),
    );
    return {
      id: String(chapter.id ?? chapter._id ?? chapter.moduleId ?? `chapter-${chapterIndex + 1}`),
      title: chapter.title ?? chapter.name ?? `Module ${chapterIndex + 1}`,
      order: Number(chapter.order ?? chapter.sequence ?? chapterIndex + 1),
      resourcesEnabled: Boolean(chapter.resourcesEnabled ?? false),
      resourcesTitle: String(chapter.resourcesTitle ?? ""),
      resources,
      quizEnabled: Boolean(chapter.quizEnabled ?? false),
      quizId: String(chapter.quizId ?? ""),
      quizAssessmentCode: String(chapter.quizAssessmentCode ?? ""),
      quizTitle: String(chapter.quizTitle ?? ""),
      lessons: lessonInput.map(normalizeLesson),
    };
  });

  const lessonsCount = chapters.reduce((count, chapter) => count + chapter.lessons.length, 0);

  const resolvedThumbnail = String(
    course.thumbnailUrl ?? course.thumbnail ?? course.image ?? "",
  ).trim();

  return {
    id: String(course.id ?? course._id ?? course.courseId),
    code: course.code,
    slug: course.slug,
    title: course.title ?? course.name ?? "Untitled Course",
    subtitle: course.subtitle ?? course.tagline ?? "",
    description: course.description ?? "",
    thumbnailUrl: resolvedThumbnail || DEFAULT_LMS_THUMBNAIL,
    bannerUrl: course.bannerUrl ?? course.banner ?? "",
    instructorName:
      course.instructorName ?? course.instructor ?? course.trainerName ?? course.authorName ?? "",
    instructorDescription: course.instructorDescription ?? "",
    guestInstructors: Array.isArray(course.guestInstructors)
      ? course.guestInstructors.map((name: unknown) => String(name ?? "")).filter(Boolean)
      : [],
    guestInstructorDescriptions: Array.isArray(course.guestInstructorDescriptions)
      ? course.guestInstructorDescriptions.map((description: unknown) => String(description ?? ""))
      : [],
    level: course.level ?? course.difficulty ?? "",
    language: course.language ?? "",
    durationInMinutes: Number(course.durationInMinutes ?? course.duration ?? 0),
    totalLessons: Number(course.totalLessons ?? lessonsCount),
    enrolled: Boolean(course.enrolled ?? course.isEnrolled),
    progressPercent: Number(course.progressPercent ?? course.progress ?? 0),
    completedLessonIds: Array.isArray(course.completedLessonIds)
      ? course.completedLessonIds.map(String)
      : [],
    finalQuizEnabled: Boolean(course.finalQuizEnabled ?? false),
    finalQuizId: String(course.finalQuizId ?? ""),
    finalQuizTitle: String(course.finalQuizTitle ?? ""),
    chapters,
  };
};

export const fetchLmsCourses = async (): Promise<LmsCourse[]> => {
  const identifiers = resolveStudentIdentifiers();
  const visibility = resolveStudentVisibility();
  const user = useAuthStore.getState().user as AnyRecord | null;
  const email = String(user?.email ?? "").trim();
  const resolvedStudentGroupIds = await resolveStudentGroupIds(
    identifiers,
    visibility.studentGroupIds,
  );
  const studentId = identifiers[0] || "";
  const response = await axios.get(`${LMS_API_PREFIX}/courses`, {
    params: {
      audience: "student",
      ...(studentId ? { studentId, identifiers: identifiers.join(",") } : {}),
      ...(email ? { email } : {}),
      ...(visibility.orgId ? { org_id: visibility.orgId } : {}),
      ...(visibility.departmentId ? { departmentId: visibility.departmentId } : {}),
      ...(resolvedStudentGroupIds.length > 0
        ? { studentGroupIds: resolvedStudentGroupIds.join(",") }
        : {}),
    },
  });
  const payload = response.data as AnyRecord;
  const resultBlock = pickObject(payload, ["result", "data"]) ?? payload;
  const rows = pickArray(resultBlock, ["courses", "items", "data"]);
  const fallbackRows = Array.isArray(resultBlock) ? resultBlock : [];

  return (rows.length > 0 ? rows : fallbackRows).map(normalizeCourse);
};

export const fetchLmsCourseById = async (courseId: string): Promise<LmsCourse> => {
  const identifiers = resolveStudentIdentifiers();
  const visibility = resolveStudentVisibility();
  const user = useAuthStore.getState().user as AnyRecord | null;
  const email = String(user?.email ?? "").trim();
  const resolvedStudentGroupIds = await resolveStudentGroupIds(
    identifiers,
    visibility.studentGroupIds,
  );
  const studentId = identifiers[0] || "";
  const response = await axios.get(`${LMS_API_PREFIX}/courses/${courseId}`, {
    params: {
      audience: "student",
      ...(studentId ? { studentId, identifiers: identifiers.join(",") } : {}),
      ...(email ? { email } : {}),
      ...(visibility.orgId ? { org_id: visibility.orgId } : {}),
      ...(visibility.departmentId ? { departmentId: visibility.departmentId } : {}),
      ...(resolvedStudentGroupIds.length > 0
        ? { studentGroupIds: resolvedStudentGroupIds.join(",") }
        : {}),
    },
  });
  const payload = response.data as AnyRecord;
  const resultBlock = pickObject(payload, ["result", "data", "course"]) ?? payload;
  return normalizeCourse(resultBlock);
};

export const enrollLmsCourse = async (payload: LmsEnrollPayload) => {
  const identifiers = resolveStudentIdentifiers();
  const visibility = resolveStudentVisibility();
  const resolvedStudentGroupIds = await resolveStudentGroupIds(
    identifiers,
    visibility.studentGroupIds,
  );
  const studentId = identifiers[0] || "";
  if (!studentId) throw new Error("Student identifier is missing. Please re-login.");
  const response = await axios.post(`${LMS_API_PREFIX}/courses/${payload.courseId}/enrollments`, {
    studentId,
    identifiers: identifiers.join(","),
    ...(visibility.orgId ? { org_id: visibility.orgId } : {}),
    audience: "student",
    ...(visibility.departmentId ? { departmentId: visibility.departmentId } : {}),
    ...(resolvedStudentGroupIds.length > 0 ? { studentGroupIds: resolvedStudentGroupIds } : {}),
  });
  return response.data;
};

export const updateLmsProgress = async (payload: LmsProgressPayload) => {
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  if (!studentId) throw new Error("Student identifier is missing. Please re-login.");
  const response = await axios.patch(`${LMS_API_PREFIX}/progress`, {
    ...payload,
    studentId,
    identifiers: identifiers.join(","),
  });
  return response.data;
};

export const syncLmsQuizResults = async (payload: { courseId: string }) => {
  const user = useAuthStore.getState().user as AnyRecord | null;
  const email = String(user?.email ?? "").trim();
  const visibility = resolveStudentVisibility();
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  if (!email || !studentId) return;

  await axios.post(`${LMS_API_PREFIX}/courses/${payload.courseId}/quiz-results/sync`, {
    studentId,
    email,
    identifiers: identifiers.join(","),
    ...(visibility.orgId ? { org_id: visibility.orgId } : {}),
  });
};

export const fetchStudentLmsQuizResults = async (
  courseId: string,
): Promise<LmsChapterQuizResult[]> => {
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  const visibility = resolveStudentVisibility();
  const response = await axios.get(`${LMS_API_PREFIX}/courses/${courseId}/quiz-results/student`, {
    params: {
      ...(studentId ? { studentId } : {}),
      ...(identifiers.length > 0 ? { identifiers: identifiers.join(",") } : {}),
      ...(visibility.orgId ? { org_id: visibility.orgId } : {}),
    },
  });
  const payload = response.data as AnyRecord;
  const resultBlock = pickObject(payload, ["result", "data"]) ?? payload;
  const rows = Array.isArray(resultBlock)
    ? resultBlock
    : pickArray(resultBlock, ["data", "items", "result"]);
  return rows.map((row: AnyRecord) => ({
    id: String(row.id ?? row._id),
    courseId: String(row.courseId ?? ""),
    chapterId: String(row.chapterId ?? ""),
    assessmentCode: String(row.assessmentCode ?? ""),
    studentId: String(row.studentId ?? ""),
    score: Number(row.score ?? 0),
    totalScore: Number(row.totalScore ?? 0),
    percentage: Number(row.percentage ?? 0),
    status: String(row.status ?? ""),
    updatedAt: String(row.updatedAt ?? ""),
    rawResult: row.rawResult ?? {},
  }));
};

export const fetchLmsQuizById = async (quizId: string): Promise<LmsQuiz> => {
  const visibility = resolveStudentVisibility();
  const response = await axios.get(`${LMS_API_PREFIX}/quizzes/${quizId}`, {
    params: {
      ...(visibility.orgId ? { org_id: visibility.orgId } : {}),
    },
  });
  const payload = response.data as AnyRecord;
  const quiz = pickObject(payload, ["result", "data"]) ?? payload;
  return {
    id: String(quiz.id ?? quiz._id),
    title: String(quiz.title ?? ""),
    description: String(quiz.description ?? ""),
    status: String(quiz.status ?? "draft") === "published" ? "published" : "draft",
    questions: pickArray(quiz, ["questions"]).map((question: AnyRecord) => ({
      id: String(question.id ?? question._id),
      question: String(question.question ?? ""),
      options: pickArray(question, ["options"]).map((option: unknown) => String(option ?? "")),
      correctOptionIndex: Number(question.correctOptionIndex ?? 0),
      marks: Number(question.marks ?? 1),
    })),
  };
};

export const submitLmsChapterQuiz = async (payload: {
  courseId: string;
  chapterId: string;
  answers: { questionId: string; selectedOptionIndex: number }[];
}) => {
  const user = useAuthStore.getState().user as AnyRecord | null;
  const visibility = resolveStudentVisibility();
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  if (!studentId) throw new Error("Student identifier is missing. Please re-login.");
  const response = await axios.post(
    `${LMS_API_PREFIX}/courses/${payload.courseId}/chapters/${payload.chapterId}/quiz-submit`,
    {
      studentId,
      email: String(user?.email ?? ""),
      studentName: String(user?.name ?? ""),
      answers: payload.answers,
      ...(visibility.orgId ? { org_id: visibility.orgId } : {}),
    },
  );
  return response.data;
};

export const submitLmsFinalQuiz = async (payload: {
  courseId: string;
  answers: { questionId: string; selectedOptionIndex: number }[];
}) => {
  const user = useAuthStore.getState().user as AnyRecord | null;
  const visibility = resolveStudentVisibility();
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  if (!studentId) throw new Error("Student identifier is missing. Please re-login.");
  const response = await axios.post(
    `${LMS_API_PREFIX}/courses/${payload.courseId}/final-quiz-submit`,
    {
      studentId,
      email: String(user?.email ?? ""),
      studentName: String(user?.name ?? ""),
      answers: payload.answers,
      ...(visibility.orgId ? { org_id: visibility.orgId } : {}),
    },
  );
  return response.data;
};

export const fetchStudentLmsCourseFeedback = async (
  courseId: string,
): Promise<LmsCourseFeedback | null> => {
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  const response = await axios.get(`${LMS_API_PREFIX}/courses/${courseId}/feedback/student`, {
    params: {
      ...(studentId ? { studentId } : {}),
    },
  });
  const payload = response.data as AnyRecord;
  const resultBlock = pickObject(payload, ["result", "data"]) ?? payload;
  const rows = Array.isArray(resultBlock)
    ? resultBlock
    : pickArray(resultBlock, ["data", "items", "result"]);
  const row = rows[0];
  if (!row) return null;
  return {
    id: String(row.id ?? row._id),
    courseId: String(row.courseId ?? ""),
    studentId: String(row.studentId ?? ""),
    studentName: String(row.studentName ?? ""),
    email: String(row.email ?? ""),
    rating: Number(row.rating ?? 0),
    status: String(row.status ?? "submitted").toLowerCase() === "skipped" ? "skipped" : "submitted",
    feedback: String(row.feedback ?? ""),
    suggestions: Array.isArray(row.suggestions)
      ? row.suggestions.map((item: unknown) => String(item ?? ""))
      : [],
    createdAt: String(row.createdAt ?? ""),
    updatedAt: String(row.updatedAt ?? ""),
  };
};

export const submitLmsCourseFeedback = async (payload: {
  courseId: string;
  status?: "submitted" | "skipped";
  rating: number;
  feedback?: string;
  suggestions?: string[];
}) => {
  const user = useAuthStore.getState().user as AnyRecord | null;
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  if (!studentId) throw new Error("Student identifier is missing. Please re-login.");
  const response = await axios.post(`${LMS_API_PREFIX}/courses/${payload.courseId}/feedback`, {
    studentId,
    email: String(user?.email ?? ""),
    studentName: String(user?.name ?? ""),
    status: payload.status ?? "submitted",
    rating: Number(payload.rating ?? 0),
    feedback: String(payload.feedback ?? ""),
    suggestions: Array.isArray(payload.suggestions) ? payload.suggestions : [],
  });
  return response.data;
};

export const fetchLmsCourseChatMessages = async (
  courseId: string,
  payload?: { limit?: number; before?: string },
): Promise<LmsCourseChatMessage[]> => {
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  const response = await axios.get(`${LMS_API_PREFIX}/courses/${courseId}/chat-messages`, {
    params: {
      ...(studentId ? { studentId } : {}),
      ...(payload?.limit ? { limit: Number(payload.limit) } : {}),
      ...(payload?.before ? { before: payload.before } : {}),
    },
  });
  const payloadData = response.data as AnyRecord;
  const resultBlock = pickObject(payloadData, ["result", "data"]) ?? payloadData;
  const rows = Array.isArray(resultBlock)
    ? resultBlock
    : pickArray(resultBlock, ["data", "items", "result"]);
  return rows.map((row: AnyRecord) => ({
    id: String(row.id ?? row._id ?? ""),
    courseId: String(row.courseId ?? ""),
    studentId: String(row.studentId ?? ""),
    studentName: String(row.studentName ?? ""),
    email: String(row.email ?? ""),
    message: String(row.message ?? ""),
    createdAt: String(row.createdAt ?? ""),
    updatedAt: String(row.updatedAt ?? ""),
  }));
};

export const sendLmsCourseChatMessage = async (payload: { courseId: string; message: string }) => {
  const user = useAuthStore.getState().user as AnyRecord | null;
  const identifiers = resolveStudentIdentifiers();
  const studentId = identifiers[0] || "";
  if (!studentId) throw new Error("Student identifier is missing. Please re-login.");
  const response = await axios.post(`${LMS_API_PREFIX}/courses/${payload.courseId}/chat-messages`, {
    studentId,
    email: String(user?.email ?? ""),
    studentName: String(user?.name ?? ""),
    message: String(payload.message ?? ""),
  });
  return response.data;
};

export const useLmsCourses = () => {
  const user = useAuthStore((state) => state.user as AnyRecord | null);
  const userScope = buildUserScope(user);
  return useQuery({
    queryKey: LMS_QUERY_KEYS.courses(userScope),
    queryFn: fetchLmsCourses,
  });
};

export const useLmsCourseDetails = (courseId: string) => {
  const user = useAuthStore((state) => state.user as AnyRecord | null);
  const userScope = buildUserScope(user);
  return useQuery({
    queryKey: LMS_QUERY_KEYS.course(courseId, userScope),
    queryFn: () => fetchLmsCourseById(courseId),
    enabled: Boolean(courseId),
  });
};

export const useEnrollLmsCourse = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user as AnyRecord | null);
  const userScope = buildUserScope(user);
  return useMutation({
    mutationFn: enrollLmsCourse,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: LMS_QUERY_KEYS.courses(userScope) });
      queryClient.invalidateQueries({
        queryKey: LMS_QUERY_KEYS.course(variables.courseId, userScope),
      });
    },
  });
};

export const useUpdateLmsProgress = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user as AnyRecord | null);
  const userScope = buildUserScope(user);
  return useMutation({
    mutationFn: updateLmsProgress,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: LMS_QUERY_KEYS.course(variables.courseId, userScope),
      });
    },
  });
};
